import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

import { isChunkLoadError } from './chunk-load-error';

const RELOAD_FLAG = 'chunk_load_retry';

function claimReload(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, '1');
    return true;
  } catch {
    // Without a durable guard, use the boundary's manual refresh instead of
    // risking a reload loop or replacing the original error with a storage error.
    return false;
  }
}

export type PreloadableLazyComponent<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  preload: () => Promise<void>;
};

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): PreloadableLazyComponent<T> {
  let modulePromise: Promise<{ default: T }> | undefined;

  const loadModule = () => {
    if (!modulePromise) {
      modulePromise = factory().catch((error) => {
        // A speculative preload may fail while the current screen is still usable.
        // Clear it so a later navigation can retry through the normal recovery path.
        modulePromise = undefined;
        throw error;
      });
    }

    return modulePromise;
  };

  const loadForRender = async () => {
    try {
      return await loadModule();
    } catch (err) {
      // On chunk fetch failure, reload once to pick up fresh index.html with updated hashes.
      // The sessionStorage flag prevents an infinite reload loop if the error is genuine.
      if (isChunkLoadError(err) && claimReload()) {
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw err;
    }
  };

  const LazyComponent = lazy(loadForRender) as PreloadableLazyComponent<T>;
  LazyComponent.preload = async () => {
    await loadModule();
  };

  return LazyComponent;
}
